import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ErrorType } from '../../../../auth/page-not-found/page-not-found.component';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { CustomMenuItem, MenuComponent } from 'src/app/@core/components/ui/menu/menu.component';
import { PopoverComponent } from 'src/app/@core/components/ui/popover/popover.component';
import { ITabPanelConfig, TabsPanelsComponent } from 'src/app/@core/components/ui/tabs-panels/tabs-panels.component';
import { CONTRACT_USER_ROLE, IMilestone, IProject, IShareContract, IShareUser, IUser, IWorkspace, SHARE_ACCESS, SHARE_FEATURE } from 'src/app/@core/models/model';
import { AuthenticationService } from 'src/app/@core/services/authentication.service';
import { CLIENT_EVENTS, SOCKET_EVENTS, SocketService } from 'src/app/@core/services/socket.service';
import saveAs from 'file-saver';
import { ToastService } from 'src/app/@core/services/toast.service';
import { OrganizationSetupFormService } from 'src/app/contract-management/services/organization-setup-from.service';
import { ShareContractService } from 'src/app/contract-management/services/share-contract.service';
import { ShareService } from 'src/app/contract-management/services/share.service';
import { UserService } from 'src/app/contract-management/services/user.service';
import { ShareAccessDialogComponent } from 'src/app/@shared/components/share-to-users/components/share-access-dialog/share-access-dialog.component';
import { NotificationsComponent } from 'src/app/@shared/components/notifications/notifications.component';
import { ScrollResetDirective } from 'src/app/@shared/directives/scroll-reset.directive';
import { AssetUrlPipe } from 'src/app/@shared/pipes/asset-url.pipe';
import { GEOTECHNICAL_ANALYSIS_SVG, SVG, TOP_BAR_SVG } from 'src/app/common/svgs';
import { SplitterModule } from 'primeng/splitter';
import { GeotechnicalAnalysisService, GeotechnicalAnalysisReport } from '../../services/geotechnical-analysis.service';
import { GeoTechnicalAnalysisReportDataService } from '../../services/geo-technical-analysis-report-data.service';
import { ShareReportComponent } from './components/share-report/share-report.component';
import { CaptureIssueComponent } from 'src/app/@shared/components/capture-issue/capture-issue.component';
import { SupportTicketFormComponent } from 'src/app/@shared/components/support-ticket-form/support-ticket-form.component';
import { createTabsConfig, createGeoTechnicalAnalysisTabsConfig, GeoTechnicalAnalysisTabConfig } from './tabs/tab-config';
import { ChatBubbleComponent } from 'src/app/@shared/components/chat-bubble/chat-bubble.component';
import { createDashboardTabsConfig } from './tabs/geo-technical-dashboard/tabs/tab-config';
import { IconComponent, IconSourceEnum } from 'src/app/@core/components/ui/icon/icon.component';
import { SupportTicketDialogService } from 'src/app/@shared/services/support-ticket-dialog.service';
import { ScreenRecordingService } from 'src/app/@shared/services/screen-recording.service';
import { SourceNavigationService } from 'src/app/@shared/services/source-navigation.service';
import { Subscription } from 'rxjs';
import { MilestonesComponent } from '../../../../contract-management/components/milestones/milestones.component';
import { FeatureFlagDirective } from "src/app/@core/directives/feature-flag.directive";
import { DatepickerComponent } from 'src/app/@core/components/ui/datepicker';
import { ContractTopBarComponent, ContractTopBarView } from '../../../../contract-management/components/edit-contract/components/contract-top-bar/contract-top-bar.component';
import { CreditsOverviewComponent } from 'src/app/features/credits/components/credits-overview/credits-overview.component';

/** @deprecated Use IPreviewReportDocument from reports-tab.component instead */
export interface IPreviewReportDocument {
  _id?: string;
  name: string;
  url: string;
  pageNumber?: number;
  _timestamp?: number;
}

@Component({
  selector: 'app-geo-technical-analysis-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ConfirmDialogModule,
    DialogModule,
    TooltipModule,
    ButtonComponent,
    MenuComponent,
    PopoverComponent,
    TabsPanelsComponent,
    ShareReportComponent,
    ShareAccessDialogComponent,
    NotificationsComponent,
    ScrollResetDirective,
    AssetUrlPipe,
    CaptureIssueComponent,
    SupportTicketFormComponent,
    IconComponent,
    MilestonesComponent,
    DatepickerComponent,
    SplitterModule,
    ChatBubbleComponent,
    CreditsOverviewComponent,
  ],
  templateUrl: './geo-technical-analysis-report.component.html',
  styleUrl: './geo-technical-analysis-report.component.scss',
  providers: [ConfirmationService],
})
export class GeoTechnicalAnalysisReportComponent implements OnInit, OnDestroy {

  @ViewChild('editableContent', { static: false }) editableContent!: ElementRef;
  @ViewChild('supportTicketForm') supportTicketFormComponent!: SupportTicketFormComponent;
  @ViewChild('captureIssue') captureIssueComponent!: CaptureIssueComponent;

  analysisId = '';
  projectId = '';
  analysisReport: GeotechnicalAnalysisReport | null = null;

  loading = true;
  currentUser: IUser | null = null;
  currentWorkspace: IWorkspace | null = null;
  isAnonymousUser = false;
  contractWorkspaceId: string | null = null;
  currentRole = '';
  isOwner = false;
  isEditor = false;
  isSameOrganization = false;
  permissions = {
    canView: false,
    canEdit: false,
    canComment: false,
    restricted: true,
  };
  fetchingRole = true;
  shareDetails: Partial<IShareContract> & { users: IShareUser[]; anyone: SHARE_ACCESS } = { users: [], anyone: SHARE_ACCESS.RESTRICTED };
  sharedUsers: IShareUser[] = [];
  externalDocument = false;
  organizationName = '';
  workspaces: IWorkspace[] = [];
  workspaceMismatchDialog = false;

  tabsConfig: GeoTechnicalAnalysisTabConfig[] = [];
  /** Default right-panel tab: "Reports" */
  activeTabValue = 'reports';
  dashboardTabsConfig: ITabPanelConfig[] = [];
  /** Default left-panel (dashboard) tab: "Report Summary" */
  dashboardActiveTabValue = 'geotechnicalsummary';
  previousDashboardTabLabel = 'Dashboard';
  setupTabsConfig: ITabPanelConfig[] = [];
  setupActiveTabValue = '1';
  isGeoTechnicalAnalysis = true;

  milestones: IMilestone[] = [];

  displayCustomDialog = false;
  dialogHeader = '';
  dialogMessage = '';
  pendingAction: 'Approve' | 'Reject' = 'Approve';
  rejectionComment = '';
  milestonesDialog = false;
  supportTicketDialog = false;
  menuOpen = false;

  // #region Panel management (splitter + collapse toggle)
  rightPanelCollapsed = false;
  currentPanelSizes: number[] = [70, 30];
  rightPanelSize = 30;
  isGutterResizing = false;
  private resizeStartSizes: number[] = [];
  // #endregion

  SVG = SVG;
  GEOTECHNICAL_ANALYSIS_SVG = GEOTECHNICAL_ANALYSIS_SVG;
  TOP_BAR_SVG = TOP_BAR_SVG;
  CONTRACT_USER_ROLE = CONTRACT_USER_ROLE;
  SHARE_FEATURE = SHARE_FEATURE;
  ContractTopBarView = ContractTopBarView;

  loadingStatuses = false;
  selectedStatus: { name: string; active?: boolean } | null = null;
  statusMenuItems: (MenuItem & { active: boolean })[] = [];
  currentDate = new Date();
  selectedDueDate: Date | null = null;
  projectDueDate: Date | null = null;
  isCurrentUserReviewedDoc = false;

  shareAccessData: {
    user: Partial<IUser>;
    visible: boolean;
    orgLevel: boolean;
    workspaceLevel: boolean;
    resource: string;
    feature: SHARE_FEATURE;
    permissions: SHARE_ACCESS[];
    onComplete: (e?: unknown) => void;
  } = {
      user: {},
      visible: false,
      orgLevel: false,
      workspaceLevel: false,
      resource: '',
      feature: SHARE_FEATURE.GEOTECHNICAL_ANALYSIS,
      permissions: [SHARE_ACCESS.VIEW, SHARE_ACCESS.EDIT, SHARE_ACCESS.COMMENT],
      onComplete: (_e?: unknown) => { },
    };

  isRecordingState = false;
  isRecording(): boolean {
    return this.isRecordingState;
  }

  items: MenuItem[] = [];
  supportMenuItems: CustomMenuItem[] = [
    { icon: SVG.RAISE_A_TICKET, iconSource: IconSourceEnum.SVG, label: 'Raise a support ticket', callback: () => this.openHelp() },
    { icon: SVG.CAPTURE_AN_ISSUE, iconSource: IconSourceEnum.SVG, label: 'Capture an issue', callback: () => this.openCapture() },
  ];
  infoMenuItems: CustomMenuItem[] = [
    { icon: TOP_BAR_SVG.DOCUMENTATION, iconSource: IconSourceEnum.SVG, label: 'Documentation', callback: () => window.open('http://help.doc.inncircles.ai/', '_blank') },
    { icon: TOP_BAR_SVG.WEBINARS, iconSource: IconSourceEnum.SVG, label: 'Webinars', callback: () => window.open('https://inncircles.com/join-webinar/', '_blank') },
    { icon: TOP_BAR_SVG.WHATS_NEW, iconSource: IconSourceEnum.SVG, label: "What's New", callback: () => window.open('https://inncircles.ai/blogs/', '_blank') },
  ];

  private sourceNavSub?: Subscription;
  private excelValidationErrorSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private geotechnicalService: GeotechnicalAnalysisService,
    public geoReportDataService: GeoTechnicalAnalysisReportDataService,
    private shareService: ShareContractService,
    private shareAccessService: ShareService,
    private authService: AuthenticationService,
    private toast: ToastService,
    private userService: UserService,
    private organizationService: OrganizationSetupFormService,
    private supportTicketDialogService: SupportTicketDialogService,
    private screenRecordingService: ScreenRecordingService,
    private confirmationService: ConfirmationService,
    private socketService: SocketService,
    private sourceNavigationService: SourceNavigationService,
  ) {
    this.organizationService.currentWorkspace.subscribe((res) => (this.currentWorkspace = res));
    this.authService.currentUser.subscribe((res) => {
      this.currentUser = res as IUser;
      this.isAnonymousUser = (res as IUser)?.anonymous ?? false;
    });
    this.route.queryParamMap.subscribe((params) => {
      const analysisId = params.get('analysisId') ?? '';
      if (analysisId && !this.isValidObjectId(analysisId)) {
        this.router.navigate(['/not-found'], { queryParams: { error: ErrorType.INVALID_URL } });
        return;
      }
      this.analysisId = analysisId;
      const projectId = params.get('projectId') ?? '';
      if (projectId && !this.isValidObjectId(projectId)) {
        this.router.navigate(['/not-found'], { queryParams: { error: ErrorType.INVALID_URL } });
        return;
      }
      this.projectId = projectId;
    });
  }

  // #region Panel management methods
  getPanelSizes(): number[] {
    return this.currentPanelSizes;
  }

  getMinSizes(): number[] {
    return [20, this.rightPanelCollapsed ? 3 : 5];
  }

  /** Position the toggle button at the gutter between the two panels */
  getRightButtonPosition(): number {
    return this.rightPanelCollapsed ? 97 : this.currentPanelSizes[0];
  }

  toggleRightPanel(): void {
    if (!this.rightPanelCollapsed) {
      // Collapse: save current size, shrink right to 3%
      this.rightPanelSize = this.currentPanelSizes[1];
      const spaceToDistribute = this.currentPanelSizes[1] - 3;
      this.currentPanelSizes = [this.currentPanelSizes[0] + spaceToDistribute, 3];
    } else {
      // Expand: restore saved size
      const spaceToTake = this.rightPanelSize - 3;
      this.currentPanelSizes = [this.currentPanelSizes[0] - spaceToTake, this.rightPanelSize];
    }
    this.rightPanelCollapsed = !this.rightPanelCollapsed;
  }

  onResizeStart(event: any): void {
    this.resizeStartSizes = event?.sizes ? [...event.sizes] : [...this.currentPanelSizes];
    this.isGutterResizing = true;
  }

  onPanelResizing(event: any): void {
    if (event?.sizes && !this.rightPanelCollapsed) {
      this.currentPanelSizes = [...event.sizes];
    }
  }

  onPanelResize(event: any): void {
    if (event?.sizes && !this.rightPanelCollapsed) {
      this.currentPanelSizes = [...event.sizes];
      this.rightPanelSize = event.sizes[1];
    }
    this.isGutterResizing = false;
  }
  // #endregion

  ngOnInit(): void {
    this.excelValidationErrorSub = this.socketService
      .on<{ errorExcel: ArrayBuffer }>(
        SOCKET_EVENTS.GEOTECHNICAL_ANALYSIS_EXCEL_VALIDATION_ERROR
      )
      .subscribe((payload) => {
        const data = Array.isArray(payload) ? payload[0] : payload;
        if (data?.errorExcel) {
          const blob = new Blob([data.errorExcel], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          saveAs(blob, 'Analysis-Excel-Errors.xlsx');
          this.toast.error('Analysis excel has validation errors. Check the downloaded file.');
        }
      });
    this.initializeData();
    this.screenRecordingService.recordingState$.subscribe((state) => {
      this.isRecordingState = state?.isRecording ?? false;
    });
    // Source navigation is now handled inside GeoTechnicalReportsTabComponent
    this.sourceNavSub = this.sourceNavigationService.navigation$.subscribe(() => {});

    this.permissions = {
      canView: false,
      canEdit: false,
      canComment: false,
      restricted: true,
    };
  }

  ngOnDestroy(): void {
    if (this.analysisId) {
      this.socketService.getSocket().then(socket => {
        socket?.emit(CLIENT_EVENTS.LEAVE_GEOTECHNICAL_ANALYSIS_ROOM, this.analysisId);
      });
    }
    this.geoReportDataService.setData(null);
    this.sourceNavSub?.unsubscribe();
    this.excelValidationErrorSub?.unsubscribe();
  }

  // Source navigation is now handled inside GeoTechnicalReportsTabComponent.
  // This stub is kept to avoid breaking the sourceNavSub subscription in ngOnInit.

  private isValidObjectId(id: string): boolean {
    return /^[a-fA-F0-9]{24}$/.test(id);
  }

  async initializeData(): Promise<void> {
    if (!this.analysisId) {
      this.toast.error('Report not found');
      return;
    }
    this.socketService.getSocket().then(socket => {
      socket?.emit(CLIENT_EVENTS.JOIN_GEOTECHNICAL_ANALYSIS_ROOM, this.analysisId);
    });
    await Promise.all([this.getWorkspacesPromise(), this.fetchReport()]);
    this.checkIfSameWorkspace();
    await this.getSharedDetails();
    this.geoReportDataService.loadFormulas();
    this.getOrganizationName()
  }

  getWorkspaces(): void {
    this.organizationService.getWorkspaces().subscribe({
      next: (res) => {
        if (res.success) this.workspaces = res.data ?? [];
      },
      error: () => (this.workspaces = []),
    });
  }

  private getWorkspacesPromise(): Promise<void> {
    return new Promise((resolve) => {
      this.organizationService.getWorkspaces().subscribe({
        next: (res) => {
          if (res.success) this.workspaces = res.data ?? [];
          resolve();
        },
        error: () => {
          this.workspaces = [];
          resolve();
        },
      });
    });
  }

  getOrganizationName(): void {
    this.organizationService.fetchOrganizationSetupData().subscribe((res) => (this.organizationName = res.data?.name ?? ''));
  }

  async getSharedDetails(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.shareService.getShareDetails(this.analysisId, SHARE_FEATURE.GEOTECHNICAL_ANALYSIS).subscribe({
        next: (res) => {
          if (res.data) {
            this.shareDetails = res.data;
            this.sharedUsers = res.data.users ?? [];
            this.externalDocument = (res.data.users ?? []).some((u: IShareUser) => (u as IShareUser & { type?: string }).type === 'EXTERNAL');
            this.getPermissionOfUser().then(() => resolve(undefined)).catch(reject);
          } else {
            this.permissions = { canView: false, canEdit: false, canComment: false, restricted: true };
            this.fetchingRole = false;
            reject('Failed to fetch share details');
          }
        },
        error: (err) => {
          this.permissions = { canView: false, canEdit: false, canComment: false, restricted: true };
          this.fetchingRole = false;
          reject(err);
        },
      });
    });
  }

  getPermissionOfUser(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.shareService.getPermissionOfUser(this.analysisId, SHARE_FEATURE.GEOTECHNICAL_ANALYSIS).subscribe({
        next: (res) => {
          if (res.success && res.data) {
            this.permissions = res.data;
            if (this.permissions.canEdit) {
              this.isEditor = true;
              this.currentRole = CONTRACT_USER_ROLE.EDITOR;
            } else if (this.permissions.canComment) {
              this.currentRole = CONTRACT_USER_ROLE.COMMENTER;
            } else if (this.permissions.canView) {
              this.currentRole = CONTRACT_USER_ROLE.VIEWER;
            }
            this.initializeTabsConfig();
            this.updateDataService();
            this.fetchingRole = false;
            resolve();
          } else {
            this.permissions = { canView: false, canEdit: false, canComment: false, restricted: true };
            this.fetchingRole = false;
            reject('Failed to fetch permissions');
          }
        },
        error: (err) => {
          this.permissions = { canView: false, canEdit: false, canComment: false, restricted: true };
          this.fetchingRole = false;
          reject(err);
        },
      });
    });
  }

  async fetchReport(): Promise<void> {
    if (!this.analysisId) return;
    this.loading = true;
    this.geotechnicalService.getReport(this.analysisId).subscribe({
      next: (res) => {
        this.analysisReport = res.data;
        this.initializeTabsConfig();
        this.projectId = this.analysisReport?.project?._id ?? '';
        if (this.analysisReport?.createdBy?._id
          == this.currentUser?._id) {
          this.isOwner = true
          this.currentRole = CONTRACT_USER_ROLE.OWNER
        }
        this.projectDueDate = this.analysisReport?.project?.dueDate ? new Date(this.analysisReport.project.dueDate) : null;
        this.isSameOrganization = this.currentUser?.organization === this.analysisReport?.organization;
        // Report document selection is now managed inside GeoTechnicalReportsTabComponent.
        this.loading = false;

        this.updateDataService();
      },
      error: () => {
        this.toast.error('Failed to load report');
        this.permissions = { canView: false, canEdit: false, canComment: false, restricted: true };
        this.loading = false;
      },
    });
  }

  private updateDataService(): void {
    const emptyDocument = !this.analysisReport?.reports?.length;
    this.geoReportDataService.setData({
      analysisReportId: this.analysisId,
      projectId: this.projectId,
      analysisReport: this.analysisReport ?? null,
      emptyDocument,
      currentRole: this.currentRole,
      isOwner: this.isOwner,
      isEditor: this.isEditor,
      permissions: this.permissions,
    });
  }

  checkIfSameWorkspace(): void {
    if (!this.analysisReport) return;
    const reportWsId = typeof (this.analysisReport as GeotechnicalAnalysisReport & { workspace?: string | IWorkspace }).workspace === 'string'
      ? (this.analysisReport as GeotechnicalAnalysisReport & { workspace: string }).workspace
      : (this.analysisReport as GeotechnicalAnalysisReport & { workspace?: IWorkspace }).workspace?._id;
    const currentWsId = this.currentWorkspace?._id;
    const hasAccess = this.workspaces.some((w) => w._id === reportWsId);
    if (reportWsId && reportWsId !== currentWsId && hasAccess && this.isSameOrganization && !this.permissions.restricted && !this.isAnonymousUser) {
      this.contractWorkspaceId = reportWsId;
      this.workspaceMismatchDialog = true;
    }
  }

  switchToContractWorkspace(): void {
    if (!this.contractWorkspaceId || !this.currentUser?._id) {
      this.toast.error('Unable to switch workspace');
      return;
    }
    this.userService.updateUser({ _id: this.currentUser._id, currentWorkspace: this.contractWorkspaceId }).subscribe({
      next: (res) => {
        if (res.success) {
          this.authService.setUserData(res.data);
          this.workspaceMismatchDialog = false;
          setTimeout(() => window.location.reload(), 1000);
        } else {
          this.toast.error('Failed to switch workspace');
        }
      },
    });
  }

  initializeTabsConfig(): void {
    this.tabsConfig = createTabsConfig(this.currentRole, CONTRACT_USER_ROLE);
    this.dashboardTabsConfig = createGeoTechnicalAnalysisTabsConfig().map((tab) => {
      if (tab.value == 'geotechnicalsummary') {
        return { ...tab, data: { analysisId: this.analysisId, summaryDescriptionInput: this.analysisReport?.reportSummaryInstructions ?? '' } }
      }
      else if (tab.value == 'geotechnicaldashboard') {
        return { ...tab, data: { analysisSummaryDescriptionInput: this.analysisReport?.analysisSummaryInstructions ?? '' } }
      }
      return tab
    }
    );
    this.setupTabsConfig = createDashboardTabsConfig();
  }

  onEnterSetup(): void {
    const active = this.dashboardTabsConfig.find(t => t.value === this.dashboardActiveTabValue);
    this.previousDashboardTabLabel = active?.label ?? 'Dashboard';
    this.geoReportDataService.setSetupMode(true);
  }

  onSetupBack(): void {
    this.geoReportDataService.setSetupMode(false);
  }

  onTabChanged(event: { value: string }): void {
    this.activeTabValue = event.value;
  }

  downloadReport(): void {
    if (this.analysisReport?.analysisExcelUrl) {
      window.open(this.analysisReport.analysisExcelUrl, '_blank');
    }
  }

  goBack(): void {
    this.router.navigate(['/geotechnical-analysis']);

  }

  navigateToLogin(): void {
    this.router.navigate(['/login']);
  }

  blurSpan(event: Event): void {
    (event.target as HTMLElement).blur();
  }

  updateReportName(event: Event): void {
    event.preventDefault();
    const target = event.target as HTMLElement;
    const newName = target.textContent?.trim() || this.analysisReport!.name || '';
    const previousName = this.analysisReport!.name ?? '';
    target.innerHTML = newName || '';
    this.analysisReport!.name = newName;
    if (!this.analysisId || newName === previousName) return;
    this.geotechnicalService.updateAnalysisName(this.analysisId, newName).subscribe({
      next: (res) => {
        if (res.data) {
          this.analysisReport = { ...this.analysisReport!, ...res.data };
          this.toast.success('Analysis name updated successfully');
        }
      },
      error: () => {
        this.analysisReport!.name = previousName;
        target.textContent = previousName;
        this.toast.error('Failed to update analysis name');
      },
    });
  }

  checkDueDatePassed(_date: Date | string | null): boolean {
    if (!_date) return false;
    const date = _date instanceof Date ? _date : new Date(_date);
    return date < this.currentDate;
    return false;
  }

  reviewDocument(): void { }

  onStatusItemClick(_item: MenuItem & { active: boolean }, _menu: PopoverComponent): void { }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  handleClick(_item: MenuItem): void { }

  onMilestoneSave(event: { edited: unknown[]; deleted: string[] }): void {
    this.milestonesDialog = false;
  }

  onMilestoneCancel(): void {
    this.milestonesDialog = false;
  }

  onSupportTicketSubmitted(): void {
    this.supportTicketDialog = false;
  }

  onSupportTicketDialogHide(): void { }

  onCaptureCompleted(_event?: unknown): void { }

  openHelp(): void {
    this.supportTicketDialogService.openDialog();
  }

  openCapture(): void {
    (this as unknown as { captureIssueComponent?: { openCapture: () => void } }).captureIssueComponent?.openCapture?.();
  }

  handleConfirmAccept(message: { accept?: () => void }): void {
    if (message.accept) message.accept();
    this.confirmationService.close();
  }

  handleConfirmReject(message: { reject?: () => void }): void {
    if (message.reject) message.reject();
    this.confirmationService.close();
  }

  onConfirmDialogAccept(): void { }

  onConfirmDialogReject(): void {
    this.displayCustomDialog = false;
  }

  handleDueDateSelect(event: any): void {
    const date: Date | null = event.value ?? null;
    if (!date || !this.analysisId) return;
    this.selectedDueDate = date;
    this.geotechnicalService.updateAnalysisDueDate(this.analysisId, date).subscribe({
      next: (res) => {
        if (res.data) {
          this.analysisReport = { ...this.analysisReport!, ...res.data };
          this.toast.success('Due date updated successfully');
        }
      },
      error: () => this.toast.error('Failed to update due date'),
    });
  }

  handleDueDateClear(): void {
    if (!this.analysisId) return;
    this.selectedDueDate = null;
    this.geotechnicalService.updateAnalysisDueDate(this.analysisId, null).subscribe({
      next: (res) => {
        if (res.data) {
          this.analysisReport = { ...this.analysisReport!, ...res.data };
          this.toast.success('Due date cleared successfully');
        }
      },
      error: () => this.toast.error('Failed to clear due date'),
    });
  }

  isSelectedPastDate(date: Date): boolean {
    return date < this.currentDate;
  }


  // fetchDocumentStatuses() {
  //   if (!this.analysisReport?.linkedDocType?._id) {
  //     return;
  //   }
  //   this.loadingStatuses = true;
  //   this.statusService.getStatuses(STATUS_TYPE.DOCUMENT, this.contract.linkedDocType._id, undefined, this.contractId).subscribe(res => {
  //     if (res && res.success) {
  //       this.documentStatuses = res.data || [];
  //       this.selectedStatus = this.documentStatuses.find(status => status._id === this.contract.documentStatus) || null;
  //       this.getStatusMenuItems();
  //     }
  //     this.loadingStatuses = false;
  //   });
  // }

  // getStatusMenuItems() {
  //   this.statusMenuItems = this.documentStatuses.map(status => ({
  //     label: status.name,
  //     command: () => this.updateContractStatus(status),
  //     class: status._id === this.selectedStatus?._id ? 'bg-arena-orange-100 text-arena-orange' : '',
  //     active: status.active
  //   }));
  // }

}
